// Utility functions for file type detection and validation

export type BrandMaterialType = 'pdf_upload' | 'txt_upload' | 'doc_upload' | 'docx_upload';

/**
 * Determines the brand material type from a File object
 * Uses MIME type first, then falls back to file extension
 */
export const getFileType = (file: File): BrandMaterialType => {
  const mimeType = file.type;

  // Try MIME type detection first (most reliable)
  if (mimeType === 'application/pdf') return 'pdf_upload';
  if (mimeType === 'text/plain') return 'txt_upload';
  if (mimeType === 'application/msword') return 'doc_upload';
  if (mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
    return 'docx_upload';
  }

  // Fallback to file extension (for cases where MIME type is not set correctly)
  const ext = file.name.split('.').pop()?.toLowerCase();
  if (ext === 'pdf') return 'pdf_upload';
  if (ext === 'txt') return 'txt_upload';
  if (ext === 'doc') return 'doc_upload';
  if (ext === 'docx') return 'docx_upload';

  // Default fallback
  return 'pdf_upload';
};

/**
 * Validates if a file is an allowed type for brand materials
 */
export const isAllowedFileType = (file: File): boolean => {
  const allowedMimeTypes = [
    'application/pdf',
    'text/plain',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ];

  const allowedExtensions = ['pdf', 'txt', 'doc', 'docx'];
  const ext = file.name.split('.').pop()?.toLowerCase();

  return allowedMimeTypes.includes(file.type) || (ext ? allowedExtensions.includes(ext) : false);
};

/**
 * Gets a human-readable name for a file type
 */
export const getFileTypeName = (type: BrandMaterialType): string => {
  const typeNames: Record<BrandMaterialType, string> = {
    pdf_upload: 'PDF',
    txt_upload: 'Text',
    doc_upload: 'Word (Legacy)',
    docx_upload: 'Word',
  };

  return typeNames[type] || 'Unknown';
};

/**
 * Gets the file extension for a file type
 */
export const getFileTypeExtension = (type: BrandMaterialType): string => {
  const extensions: Record<BrandMaterialType, string> = {
    pdf_upload: '.pdf',
    txt_upload: '.txt',
    doc_upload: '.doc',
    docx_upload: '.docx',
  };

  return extensions[type] || '';
};
